import React, { useCallback, useEffect, useState } from "react";
import {
  UserCog, Calendar, Banknote, Target, ClipboardCheck,
  Folders, Heart, MessageSquare, Video, GraduationCap,
  Gavel,
  Check, AlertTriangle,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const cardCls =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const inputCls =
  "w-full p-2.5 rounded-lg bg-black/40 border border-white/20 text-white placeholder-white/30 " +
  "outline-none focus:border-[#00FF99]/60 text-sm";

// Module catalogue. Keys MUST match the backend ALL_MODULES list.
// Pricing per seat per month — placeholder until backend module-pricing exists.
//
// ======================================================================
// THIS IS THE THIRD COPY OF THE MODULE LIST, AND THAT IS THE DEFECT
// ======================================================================
//
// The same catalogue is written down four times, in four shapes:
//
//   modules/routes.py ALL_MODULES        13 keys. AUTHORITATIVE — it is
//                                        what /api/modules answers with,
//                                        and the only one that decides
//                                        anything.
//   this file                            keys + names + icons + PRICES.
//                                        The only place a price exists.
//   admin/AdminModulesPage.jsx           keys + names + icons +
//                                        descriptions + a `protected`
//                                        flag, for the tenant's own admin.
//   superadmin/SuperadminDemosPage.jsx   keys + labels, and a DIFFERENT
//                                        vocabulary — see that file.
//
// Nothing reconciles them. er_cases was added to ALL_MODULES by task 1
// and to none of the three frontend copies, which is exactly why the key
// existed, defaulted to off, and had no control anywhere that could turn
// it on. Adding a module means editing all four, and there is no test
// that fails when you forget.
//
// A key here that the backend does not know is ignored by the PATCH; a
// key the backend knows and this list omits is unmanageable from this
// screen. Both of those are silent. THREE keys are in that second
// state today even after this change — expenses, benefits and
// er_cases's absence was the third — see the report.
const MODULES = [
  { key: "core_hr",         name: "Core HR",            icon: UserCog,         price: 2 },
  { key: "leave",           name: "Leave Management",   icon: Calendar,        price: 1 },
  { key: "payroll",         name: "Payroll",            icon: Banknote,        price: 3 },
  { key: "performance",     name: "Performance",        icon: Target,          price: 2 },
  { key: "attendance",      name: "Attendance",         icon: ClipboardCheck,  price: 1 },
  { key: "projects",        name: "Projects & Tasks",   icon: Folders,         price: 2 },
  { key: "engagement",      name: "Engagement",         icon: Heart,           price: 1 },
  { key: "ai_chat",         name: "KweryAI Chat",       icon: MessageSquare,   price: 4 },
  { key: "video_messaging", name: "Video & Messaging",  icon: Video,           price: 2 },
  { key: "learning",        name: "Learning",           icon: GraduationCap,   price: 2 },
  // ER case management (task 16). £3/seat/month, the same as Payroll.
  //
  // DEFAULT-OFF AT THE BACKEND, and this card is how it gets turned on.
  // modules/routes.py puts er_cases in DEFAULT_OFF_MODULES so an org with
  // no org_modules row resolves to DISABLED — every other key resolves to
  // enabled. That inversion exists because hooks/useOrgModules.js is
  // fail-open for unknown keys, so without it the Cases entry would
  // appear for every tenant on every plan.
  //
  // NOT PROTECTED, deliberately. core_hr and ai_chat are, so a tenant
  // admin cannot switch off their own org's. ER is optional: a tenant
  // that has not bought it should have it off and see nothing.
  //
  // Gavel is the icon the CASES SIDEBAR ENTRY already uses
  // (components/chat/Sidebar.jsx). An operator enabling the module here
  // sees the same glyph that will appear in the user's sidebar, which is
  // worth more than picking a scale or a file-search for variety.
  //
  // PER-SEAT, NOT PER-CASE. Per-case pricing was considered and
  // deferred; there is no metering here and none should be added
  // without deciding what a "case" is for billing — opened, closed,
  // reopened, or open on the billing date are four different numbers.
  { key: "er_cases",        name: "ER Cases",           icon: Gavel,           price: 3 },
];

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      className={
        "relative inline-flex items-center h-6 w-11 rounded-full transition " +
        (disabled ? "opacity-50 cursor-not-allowed " : "") +
        (on ? "bg-[#00FF99]" : "bg-white/15")
      }
      aria-pressed={on}
    >
      <span
        className={
          "inline-block h-4 w-4 rounded-full bg-black transform transition " +
          (on ? "translate-x-6" : "translate-x-1")
        }
      />
    </button>
  );
}

export default function SuperadminModules() {
  const [orgs, setOrgs]     = useState([]);
  const [orgId, setOrgId]   = useState("");
  const [enabled, setEnabled] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey]   = useState(null);
  const [error, setError]   = useState("");

  // Load orgs once
  useEffect(() => {
    fetch(`${API_BASE}/api/onboarding/orgs`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setOrgs(list);
        if (list[0]) setOrgId(list[0].id);
      })
      .catch(() => {});
  }, []);

  // Load module state whenever org changes
  const loadModules = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/modules/${id}`, { headers: authHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || `Failed to load modules (${res.status})`);
        setEnabled({});
        return;
      }
      const data = await res.json();
      const map = {};
      for (const m of data.modules || []) map[m.module_key] = !!m.is_enabled;
      setEnabled(map);
    } catch {
      setError("Network error loading modules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadModules(orgId); }, [orgId, loadModules]);

  // Toggle handler — optimistic, with rollback on failure
  const handleToggle = async (key, next) => {
    const prev = !!enabled[key];
    setEnabled((s) => ({ ...s, [key]: next }));
    setSavingKey(key);
    setSavedKey(null);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/modules/${orgId}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({
          modules: [{ module_key: key, is_enabled: next }],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Save failed (${res.status})`);
      }
      setSavedKey(key);
      setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 1500);
    } catch (e) {
      // revert
      setEnabled((s) => ({ ...s, [key]: prev }));
      setError(e.message || "Save failed.");
    } finally {
      setSavingKey((k) => (k === key ? null : k));
    }
  };

  // BOTH FIGURES DERIVE FROM `MODULES`, and were already doing so before
  // task 16 — neither the count, the denominator nor the total is
  // hardcoded, so adding the ER card moved all three without touching
  // this block. Recorded because "10 / 10" and "£20" look like written
  // constants on screen and the obvious next edit is to type the new
  // numbers in.
  //
  // THE TOTAL IS THE SUM OF ENABLED MODULES ONLY. A tenant with ER
  // switched off is not quoted for it. `enabled` comes from
  // GET /api/modules/{org_id}, which returns an explicit is_enabled for
  // every key in the backend's ALL_MODULES — including `false` for a
  // default-off module the org has no row for — so a missing row reads
  // as off here rather than as undefined.
  const totalCost = MODULES
    .filter((m) => enabled[m.key])
    .reduce((sum, m) => sum + m.price, 0);

  const enabledCount = MODULES.filter((m) => enabled[m.key]).length;

  return (
    <div className="space-y-4">
      {/* Org selector + summary */}
      <div className={`${cardCls} p-4 flex flex-wrap items-center gap-4`}>
        <div className="flex-1 min-w-[260px]">
          <label className="text-xs text-white/60 uppercase tracking-wider block mb-1">
            Organisation
          </label>
          <select
            className={inputCls + " max-w-md"}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {orgs.length === 0 && <option value="">No organisations</option>}
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name || o.slug}</option>
            ))}
          </select>
        </div>
        <div className="text-right">
          <div className="text-xs text-white/50 uppercase tracking-wider">Modules enabled</div>
          <div className="text-2xl font-bold text-[#00FF99]">
            {loading ? "…" : `${enabledCount} / ${MODULES.length}`}
          </div>
          <div className="text-xs text-white/50 mt-1">£{totalCost} per seat / month</div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-2 text-sm flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Module grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MODULES.map((m) => {
          const Icon = m.icon;
          const on = !!enabled[m.key];
          const isSaving = savingKey === m.key;
          const isSaved  = savedKey === m.key;
          return (
            <div key={m.key} className={`${cardCls} p-5 flex items-start justify-between gap-4`}>
              <div className="flex gap-3 min-w-0">
                <div className={
                  "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 " +
                  (on ? "bg-[#00FF99]/10 text-[#00FF99]" : "bg-white/5 text-white/40")
                }>
                  <Icon size={20} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{m.name}</div>
                  <div className="text-xs text-white/50 mt-0.5">£{m.price}/seat/month</div>
                  {isSaving && (
                    <div className="text-xs text-white/40 mt-1">Saving…</div>
                  )}
                  {isSaved && (
                    <div className="text-xs text-[#00FF99] mt-1 flex items-center gap-1">
                      <Check size={12} /> Saved
                    </div>
                  )}
                </div>
              </div>
              <Toggle
                on={on}
                onChange={(next) => handleToggle(m.key, next)}
                disabled={isSaving || loading || !orgId}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
